import { useEffect, useState, useRef } from 'react';
import { NavLink } from 'react-router-dom';
import { createPortal } from 'react-dom';

/**
 * Mobile navigation drawer — hamburger trigger + sliding side panel.
 *
 * Behaviour:
 *   - Trigger button visible only at viewport ≤ 768px (CSS handles).
 *   - Drawer mounts in a portal to escape z-index of the page header.
 *   - Body scroll locked while drawer is open (`body.no-scroll`).
 *   - Backdrop click + Escape both close the drawer.
 *   - Clicking any nav link auto-closes the drawer.
 *   - Focus moves into the drawer on open, returns to the trigger on close.
 */

const NAV_ITEMS = [
  { to: '/dashboard',          label: 'Dashboard' },
  { to: '/hr/employees',       label: 'Employees' },
  { to: '/hr/leave',           label: 'Leave' },
  { to: '/finance/journal',    label: 'Finance' },
  { to: '/procurement/requests', label: 'Procurement' },
  { to: '/pm/projects',        label: 'Projects' },
  { to: '/wiki',               label: 'Wiki' },
  { to: '/crm/pipeline',       label: 'CRM' },
  { to: '/dms',                label: 'Documents' },
  { to: '/messenger',          label: 'Messenger' },
  { to: '/analytics',          label: 'Analytics' },
  { to: '/events',             label: 'Events' },
  { to: '/admin',              label: 'Admin' },
  { to: '/workflows/my-approvals', label: 'My approvals' },
  { to: '/plus',               label: 'All modules' },
];

export default function MobileNav() {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef(null);
  const closeRef = useRef(null);

  // Body scroll lock while open.
  useEffect(() => {
    if (open) document.body.classList.add('no-scroll');
    else document.body.classList.remove('no-scroll');
    return () => document.body.classList.remove('no-scroll');
  }, [open]);

  // Escape closes drawer.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') { e.preventDefault(); setOpen(false); } };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  // Focus management: move focus into drawer on open, return to trigger on close.
  useEffect(() => {
    if (open) {
      queueMicrotask(() => closeRef.current?.focus());
    } else if (triggerRef.current && document.activeElement === document.body) {
      triggerRef.current.focus();
    }
  }, [open]);

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className="hamburger-btn"
        data-testid="hamburger-menu"
        aria-label="Open navigation menu"
        aria-expanded={open}
        aria-controls="mobile-nav-drawer"
        onClick={() => setOpen(true)}
      >
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <line x1="3" y1="6" x2="21" y2="6" />
          <line x1="3" y1="12" x2="21" y2="12" />
          <line x1="3" y1="18" x2="21" y2="18" />
        </svg>
      </button>

      {open && createPortal(
        <>
          <div
            className="mobile-nav-backdrop"
            data-testid="nav-backdrop"
            onClick={() => setOpen(false)}
            aria-hidden="true"
          />
          <aside
            id="mobile-nav-drawer"
            className="mobile-nav-drawer"
            data-testid="mobile-nav-drawer"
            role="dialog"
            aria-modal="true"
            aria-label="Navigation menu"
          >
            <header>
              <strong>Menu</strong>
              <button
                ref={closeRef}
                type="button"
                className="btn ghost small"
                aria-label="Close navigation menu"
                onClick={() => setOpen(false)}
              >
                ×
              </button>
            </header>
            <nav aria-label="Primary">
              {NAV_ITEMS.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.to === '/dashboard'}
                  onClick={() => setOpen(false)}
                  className={({ isActive }) => (isActive ? 'active' : '')}
                >
                  {item.label}
                </NavLink>
              ))}
            </nav>
          </aside>
        </>,
        document.body
      )}
    </>
  );
}
